import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CartModel from '../../../../models/CartModel';
import NoActivityPlaceholder from './NoActivityPlaceholder';
import LoadingMC from '../../../../components/LoadingMC';
import IconMC from '../../../../components/IconMC';
import { cartActionErrorMessage, cartTotal, formatCartPrice, lineTotal } from './cartFormat';
import CartQuantityStepper, { MAX_CART_QUANTITY } from './CartQuantityStepper';
import { showtoast } from '../../../../../util/toast';

const itemKey = (item) => `${item.product_id}-${item.variant_id || 'none'}`;

const removeButtonStyle = (disabled) => ({
  width: 28,
  height: 28,
  padding: 0,
  border: '1px solid #D6DCE1',
  background: '#fff',
  borderRadius: 8,
  cursor: disabled ? 'not-allowed' : 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  opacity: disabled ? 0.4 : 1
});

const CartCurrent = ({ refreshKey, customerId }) => {
  const { t } = useTranslation('ChatbotIA');
  const { customer } = useSelector((reduxState) => reduxState.mainReducer);
  const { retailer_info } = useSelector((reduxState) => reduxState.retailerUsersReducer);
  const currency = retailer_info?.currency_code ? `${retailer_info.currency_code} ` : '';
  const customerIdToUse = customerId !== undefined ? customerId : customer?.id;
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(Boolean(customerIdToUse));
  const [busyKey, setBusyKey] = useState(null);
  const items = cart?.items || [];
  const total = cartTotal(items);

  useEffect(() => {
    if (!customerIdToUse) {
      setCart({ items: [] });
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    CartModel.retrieve(String(customerIdToUse))
      .then((response) => {
        if (cancelled) return;
        setCart(response || { items: [] });
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Error fetching cart', error);
        setCart({ items: [] });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [refreshKey, customerIdToUse]);

  const applyCart = (response) => {
    setCart(response || { items: [] });
  };

  const handleRemove = (item) => {
    if (!customerIdToUse) return;

    const key = itemKey(item);
    setBusyKey(key);
    CartModel.removeItems(String(customerIdToUse), [{
      product_id: item.product_id,
      variant_id: item.variant_id ?? null,
      quantity: item.quantity
    }])
      .then(applyCart)
      .catch((error) => {
        showtoast(error?.message || t('cart.playground.remove_error'));
      })
      .finally(() => {
        setBusyKey(null);
      });
  };

  const handleUpdateQuantity = (item, quantity) => {
    if (!customerIdToUse) return;

    const nextQuantity = parseInt(quantity, 10);
    if (
      !nextQuantity
      || nextQuantity < 1
      || nextQuantity > MAX_CART_QUANTITY
      || nextQuantity === Number(item.quantity)
    ) {
      return;
    }

    const key = itemKey(item);
    setBusyKey(key);
    CartModel.updateItems(String(customerIdToUse), [{
      product_id: item.product_id,
      variant_id: item.variant_id ?? null,
      quantity: nextQuantity
    }])
      .then(applyCart)
      .catch((error) => {
        showtoast(cartActionErrorMessage(error, t, 'cart.playground.update_error'));
      })
      .finally(() => {
        setBusyKey(null);
      });
  };

  if (!loading && !items.length) {
    return <NoActivityPlaceholder />;
  }

  if (loading && !items.length) {
    return (
      <div className="tw-flex tw-justify-center tw-py-4">
        <LoadingMC loading />
      </div>
    );
  }

  return (
    <div className="tw-wrapper" style={{ marginTop: 16 }}>
      <ul className="p-0 m-0">
        {items.map((item, index) => {
          const key = itemKey(item);
          const busy = busyKey === key;
          const quantity = Number(item.quantity) || 1;

          return (
            <li
              key={`${key}-${index}`}
              className="d-flex align-items-start"
              style={{
                gap: 8,
                padding: '10px 0',
                borderBottom: index === items.length - 1 ? 'none' : '1px solid #F3F4F6'
              }}
            >
              <div
                className="d-flex align-items-center justify-content-center flex-shrink-0"
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: '#F7F8FD'
                }}
              >
                <IconMC name="shopping-cart-outline" size="base" className="tw-text-blue-500" />
              </div>

              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div className="d-flex align-items-start justify-content-between" style={{ gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div className="fz-12 font-600 text-gray-950" style={{ wordBreak: 'break-word' }}>
                      {item.name}
                    </div>
                    {item.variant_name && (
                      <div className="fz-11" style={{ color: '#6b7280' }}>{item.variant_name}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    style={removeButtonStyle(busy)}
                    onClick={() => handleRemove(item)}
                    disabled={busy}
                    aria-label={t('cart.playground.remove_product')}
                  >
                    <IconMC name="trash-2-outline" size="sm" className="tw-text-red-500" />
                  </button>
                </div>

                <div
                  className="d-flex align-items-center justify-content-between"
                  style={{ gap: 8, marginTop: 8 }}
                >
                  <CartQuantityStepper
                    quantity={quantity}
                    disabled={busy}
                    onDecrease={() => handleUpdateQuantity(item, quantity - 1)}
                    onIncrease={() => handleUpdateQuantity(item, quantity + 1)}
                  />
                  <span className="fz-12 font-600 text-gray-950" style={{ whiteSpace: 'nowrap' }}>
                    {formatCartPrice(currency, lineTotal(item))}
                  </span>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      <div
        className="d-flex align-items-center justify-content-between"
        style={{ padding: '10px 0 4px', borderTop: '1px dashed #E5E7EB', marginTop: 4 }}
      >
        <span className="fz-12 font-600" style={{ color: '#374151' }}>{t('cart.inbox.grand_total')}</span>
        <span className="fz-12 font-600 text-gray-950">{formatCartPrice(currency, total)}</span>
      </div>
    </div>
  );
};

export default CartCurrent;
